import { useState, useEffect, FormEvent } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import Badge from '@/components/common/Badge'
import StrategySelector from '@/components/teams/StrategySelector'
import FeedbackLoopConfig from '@/components/teams/FeedbackLoopConfig'
import TeamMemberSelector from '@/components/teams/TeamMemberSelector'
import { teamStore } from '@/stores/TeamStore'
import { workspaceStore } from '@/stores/WorkspaceStore'
import { agentStore } from '@/stores/AgentStore'
import {
  TeamCoordinationStrategy,
  TeamStatus,
  AgentTeamCreate,
} from '@/types'
import { ArrowLeftIcon, CheckIcon, SparklesIcon } from '@heroicons/react/24/outline'
import type { TeamTemplate } from '@/constants/teamTemplates'

const TeamEditorPage = observer(() => {
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  const isEditMode = !!id

  // Check if we're initializing from a template
  const template = location.state?.template as TeamTemplate | undefined

  const [formData, setFormData] = useState<AgentTeamCreate>({
    name: template?.teamConfig.name || '',
    description: template?.teamConfig.description || '',
    strategy: template?.teamConfig.strategy || TeamCoordinationStrategy.SEQUENTIAL,
    coordinator_agent_id: undefined,
    configuration: template?.teamConfig.configuration || {},
    feedback_enabled: template?.teamConfig.feedback_enabled || false,
    max_iterations: template?.teamConfig.max_iterations || 3,
    members: template?.teamConfig.members || [],
  })

  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    // Fetch agents for the member selector
    agentStore.fetchAgents()

    // If editing, load the team
    if (isEditMode && id) {
      loadTeam(id)
    }
  }, [id, isEditMode])

  const loadTeam = async (teamId: string) => {
    try {
      await teamStore.fetchTeamById(teamId)
      if (teamStore.selectedTeam) {
        setFormData({
          name: teamStore.selectedTeam.name,
          description: teamStore.selectedTeam.description,
          strategy: teamStore.selectedTeam.strategy,
          coordinator_agent_id: teamStore.selectedTeam.coordinator_agent_id,
          configuration: teamStore.selectedTeam.configuration,
          feedback_enabled: teamStore.selectedTeam.feedback_enabled,
          max_iterations: teamStore.selectedTeam.max_iterations,
          members: teamStore.selectedTeam.members.map((m) => ({
            agent_id: m.agent_id,
            role: m.role,
            role_description: m.role_description,
            execution_order: m.execution_order,
            is_required: m.is_required,
            can_delegate: m.can_delegate,
            feedback_sources: m.feedback_sources,
            feedback_targets: m.feedback_targets,
            configuration: m.configuration,
          })),
        })
      }
    } catch (error) {
      toast.error('Failed to load team')
      navigate('/teams')
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      toast.error('Team name is required')
      return
    }

    if (!formData.members || formData.members.length === 0) {
      toast.error('Add at least one team member')
      return
    }

    if (formData.feedback_enabled && (!formData.max_iterations || formData.max_iterations < 1)) {
      toast.error('Max iterations must be at least 1 when feedback loop is enabled')
      return
    }

    setIsSaving(true)

    try {
      if (isEditMode && id) {
        await teamStore.updateTeam(id, {
          name: formData.name,
          description: formData.description,
          strategy: formData.strategy,
          coordinator_agent_id: formData.coordinator_agent_id,
          configuration: formData.configuration,
          feedback_enabled: formData.feedback_enabled,
          max_iterations: formData.max_iterations,
          status: TeamStatus.ACTIVE,
        })
        toast.success('Team updated successfully!')
      } else {
        await teamStore.createTeam({
          ...formData,
          workspace_id: workspaceStore.currentWorkspace?.id,
        })
        toast.success('Team created successfully!')
      }
      navigate('/teams')
    } catch (error: any) {
      toast.error(error.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'create'} team`)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    navigate('/teams')
  }

  return (
    <div className="space-y-6">
      {/* Template Badge */}
      {template && !isEditMode && (
        <Card className="bg-gradient-to-r from-primary-50 to-indigo-50 border-primary-200">
          <div className="flex items-center gap-3">
            <SparklesIcon className="w-6 h-6 text-primary-600" />
            <div>
              <p className="text-sm font-semibold text-primary-900">
                Creating from template: {template.icon} {template.name}
              </p>
              <p className="text-xs text-primary-700 mt-0.5">
                {template.description}
              </p>
            </div>
            <div className="ml-auto">
              <Badge variant="primary">
                Template
              </Badge>
            </div>
          </div>
        </Card>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="secondary" size="sm" onClick={handleCancel}>
            <ArrowLeftIcon className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {isEditMode ? 'Edit Team' : template ? `Create Team from Template` : 'Create New Team'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isEditMode
                ? 'Update your multi-agent team configuration'
                : template
                  ? 'Customize the pre-configured template to match your needs'
                  : 'Configure a new multi-agent team with specialized roles'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <h2 className="text-lg font-semibold text-foreground mb-4">Basic Information</h2>
          <div className="space-y-4">
            <Input
              label="Team Name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Full-Stack Development Team"
              required
              disabled={isSaving}
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what this team does and when to use it..."
                rows={3}
                disabled={isSaving}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-muted/50 disabled:text-muted-foreground"
              />
            </div>

            {agentStore.agents.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Coordinator Agent (Optional)
                </label>
                <select
                  value={formData.coordinator_agent_id || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      coordinator_agent_id: e.target.value || undefined,
                    })
                  }
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-muted/50"
                >
                  <option value="">-- No coordinator (members coordinate directly) --</option>
                  {agentStore.agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name} ({agent.framework})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  For hierarchical strategy, coordinator delegates tasks to team members
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Coordination Strategy */}
        <Card>
          <h2 className="text-lg font-semibold text-foreground mb-4">Coordination Strategy</h2>
          <StrategySelector
            value={formData.strategy}
            onChange={(strategy) => setFormData({ ...formData, strategy })}
            disabled={isSaving}
          />
        </Card>

        {/* Feedback Loop Configuration */}
        <Card>
          <h2 className="text-lg font-semibold text-foreground mb-4">Feedback Loop Configuration</h2>
          <FeedbackLoopConfig
            enabled={formData.feedback_enabled || false}
            maxIterations={formData.max_iterations}
            onEnabledChange={(enabled) => setFormData({ ...formData, feedback_enabled: enabled })}
            onMaxIterationsChange={(max) => setFormData({ ...formData, max_iterations: max })}
            disabled={isSaving}
          />
        </Card>

        {/* Team Members */}
        <Card>
          <h2 className="text-lg font-semibold text-foreground mb-4">Team Members</h2>
          <TeamMemberSelector
            members={formData.members || []}
            onChange={(members) => setFormData({ ...formData, members })}
            disabled={isSaving}
          />
        </Card>

        {/* Actions */}
        <Card>
          <div className="flex items-center justify-between">
            <Button variant="secondary" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSaving || !formData.name.trim() || !formData.members || formData.members.length === 0}
            >
              <CheckIcon className="w-5 h-5 mr-2" />
              {isSaving ? 'Saving...' : isEditMode ? 'Update Team' : 'Create Team'}
            </Button>
          </div>
        </Card>
      </form>
    </div>
  )
})

export default TeamEditorPage
